// src/pages/emailMarketing/CampaignStatsPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import emailMarketingService from '../../helpers/emailMarketingService';
import Swal from 'sweetalert2';
import { FaPaperPlane, FaEnvelopeOpen, FaMousePointer } from 'react-icons/fa';
import {
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend
} from 'recharts';

function CampaignStatsPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [campaign, setCampaign] = useState(null);
    const [stats, setStats] = useState(null);
    const [engagementStats, setEngagementStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [resending, setResending] = useState(false);
    const [showResendDialog, setShowResendDialog] = useState(false);
    const [newSubject, setNewSubject] = useState('');

    useEffect(() => {
        fetchStats();
        // Si está sending, hacer polling cada 5 segundos
        let interval;
        if (campaign?.status === 'sending') {
            interval = setInterval(fetchStats, 5000);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [id, campaign?.status]);

    const fetchStats = async () => {
        try {
            const campaignData = await emailMarketingService.getCampaign(id);
            const statsData = await emailMarketingService.getCampaignStats(id);
            
            setCampaign(campaignData);
            setStats(statsData);
            
            // Obtener estadísticas de engagement si la campaña está enviada
            if (campaignData.marcaId && (campaignData.status === 'sent' || campaignData.status === 'sending')) {
                try {
                    const engagementData = await emailMarketingService.getEngagementStats(id, campaignData.marcaId);
                    setEngagementStats(engagementData);
                } catch (error) {
                    // Si no hay datos de engagement aún, no es un error crítico
                    setEngagementStats(null);
                }
            }
        } catch (error) {
            Swal.fire('Error', error.message, 'error');
            navigate('/email-marketing/campaigns');
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        if (!campaign?.marcaId) {
            Swal.fire('Error', 'No se puede reenviar: falta información de la marca', 'error');
            return;
        }

        setResending(true);
        try {
            const result = await emailMarketingService.resendToNonOpeners(
                id,
                campaign.marcaId,
                {
                    delayDays: 3,
                    newSubject: newSubject || undefined
                }
            );

            Swal.fire({
                title: 'Reenvío completado',
                html: `
                    <p>Se reenvió la campaña a <strong>${result.resentCount}</strong> recipients que no abrieron el email original.</p>
                    ${result.skippedCount > 0 ? `<p class="text-sm mt-2">Se omitieron ${result.skippedCount} recipients.</p>` : ''}
                `,
                icon: 'success',
                confirmButtonText: 'Entendido'
            });

            setShowResendDialog(false);
            setNewSubject('');
            // Refrescar estadísticas
            fetchStats();
        } catch (error) {
            Swal.fire('Error', error.message || 'No se pudo reenviar la campaña', 'error');
        } finally {
            setResending(false);
        }
    };

    const getEngagementColor = (rate) => {
        if (rate >= 25) return 'text-green-600';
        if (rate >= 15) return 'text-yellow-600';
        return 'text-red-600';
    };

    const getEngagementBgColor = (rate) => {
        if (rate >= 25) return 'bg-green-50';
        if (rate >= 15) return 'bg-yellow-50';
        return 'bg-red-50';
    };

    if (loading) {
        return (
            <div className="container mx-auto px-4 py-6">
                <div className="text-center">Cargando...</div>
            </div>
        );
    }

    if (!campaign || !stats) {
        return (
            <div className="container mx-auto px-4 py-6">
                <div className="text-center">No se encontró la campaña</div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-6">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold">Estadísticas: {campaign.name}</h1>
                <button
                    onClick={() => navigate('/email-marketing/campaigns')}
                    className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
                >
                    Volver
                </button>
            </div>

            {/* Cards de métricas básicas */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white p-6 rounded-lg shadow">
                    <p className="text-gray-600 text-sm">Total Destinatarios</p>
                    <p className="text-2xl font-bold">{stats.totalRecipients}</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow">
                    <p className="text-gray-600 text-sm">Enviados</p>
                    <p className="text-2xl font-bold text-green-600">{stats.sentCount}</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow">
                    <p className="text-gray-600 text-sm">Fallidos</p>
                    <p className="text-2xl font-bold text-red-600">{stats.failedCount}</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow">
                    <p className="text-gray-600 text-sm">Pendientes</p>
                    <p className="text-2xl font-bold text-yellow-600">{stats.pendingCount}</p>
                </div>
            </div>

            {/* Estadísticas de Engagement */}
            {(engagementStats || (campaign.status === 'sent' || campaign.status === 'sending')) && (
                <div className="bg-white p-6 rounded-lg shadow mb-6">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-xl font-bold flex items-center">
                            <FaEnvelopeOpen className="mr-2 text-blue-500" />
                            Engagement
                        </h2>
                        {campaign.status === 'sent' && engagementStats && engagementStats.opened < engagementStats.totalRecipients && (
                            <button
                                onClick={() => setShowResendDialog(true)}
                                className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 flex items-center"
                                title="Reenvía la campaña a recipients que no abrieron el email original"
                            >
                                <FaPaperPlane className="mr-2" />
                                Reenviar a No Abridores ({engagementStats.totalRecipients - engagementStats.opened})
                            </button>
                        )}
                    </div>
                    
                    {engagementStats ? (
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className={`p-4 rounded-lg ${getEngagementBgColor(engagementStats.openRate)}`}>
                            <div className="flex items-center mb-2">
                                <FaEnvelopeOpen className={`mr-2 ${getEngagementColor(engagementStats.openRate)}`} />
                                <p className="text-gray-600 text-sm font-medium">Open Rate</p>
                            </div>
                            <p className={`text-3xl font-bold ${getEngagementColor(engagementStats.openRate)}`}>
                                {engagementStats.openRate.toFixed(1)}%
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                                {engagementStats.opened} de {engagementStats.totalRecipients} abrieron
                            </p>
                        </div>
                        
                        <div className={`p-4 rounded-lg ${getEngagementBgColor(engagementStats.clickRate)}`}>
                            <div className="flex items-center mb-2">
                                <FaMousePointer className={`mr-2 ${getEngagementColor(engagementStats.clickRate)}`} />
                                <p className="text-gray-600 text-sm font-medium">Click Rate</p>
                            </div>
                            <p className={`text-3xl font-bold ${getEngagementColor(engagementStats.clickRate)}`}>
                                {engagementStats.clickRate.toFixed(1)}%
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                                {engagementStats.clicked} de {engagementStats.totalRecipients} clickearon
                            </p>
                        </div>
                        
                        <div className="p-4 rounded-lg bg-blue-50">
                            <div className="flex items-center mb-2">
                                <FaMousePointer className="mr-2 text-blue-600" />
                                <p className="text-gray-600 text-sm font-medium">Click-to-Open Rate</p>
                            </div>
                            <p className="text-3xl font-bold text-blue-600">
                                {engagementStats.clickToOpenRate.toFixed(1)}%
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                                {engagementStats.clicked} de {engagementStats.opened} que abrieron clickearon
                            </p>
                        </div>
                    </div>
                    ) : (
                        <div className="text-center py-8 text-gray-500">
                            <FaEnvelopeOpen className="mx-auto text-4xl mb-4 text-gray-300" />
                            <p>Los datos de engagement se están procesando...</p>
                            <p className="text-sm mt-2">Vuelve a revisar en unos momentos.</p>
                        </div>
                    )}
                </div>
            )}

            {/* Gráfico de distribución */}
            <div className="bg-white p-6 rounded-lg shadow mb-6">
                <h2 className="text-xl font-bold mb-4">Distribución de Estados</h2>
                <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie
                                data={[
                                    { name: 'Enviados', value: stats.sentCount, color: '#10B981' },
                                    { name: 'Fallidos', value: stats.failedCount, color: '#EF4444' },
                                    { name: 'Pendientes', value: stats.pendingCount, color: '#F59E0B' },
                                    { name: 'Bajas', value: stats.unsubscribedCount || 0, color: '#6B7280' }
                                ]}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                                outerRadius={80}
                                fill="#8884d8"
                                dataKey="value"
                            >
                                {[
                                    { name: 'Enviados', value: stats.sentCount, color: '#10B981' },
                                    { name: 'Fallidos', value: stats.failedCount, color: '#EF4444' },
                                    { name: 'Pendientes', value: stats.pendingCount, color: '#F59E0B' },
                                    { name: 'Bajas', value: stats.unsubscribedCount || 0, color: '#6B7280' }
                                ].map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                ))}
                            </Pie>
                            <Tooltip />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Gráfico de barras */}
            <div className="bg-white p-6 rounded-lg shadow mb-6">
                <h2 className="text-xl font-bold mb-4">Métricas de Envío</h2>
                <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                            data={[
                                { name: 'Enviados', value: stats.sentCount, color: '#10B981' },
                                { name: 'Fallidos', value: stats.failedCount, color: '#EF4444' },
                                { name: 'Pendientes', value: stats.pendingCount, color: '#F59E0B' },
                                { name: 'Bajas', value: stats.unsubscribedCount || 0, color: '#6B7280' }
                            ]}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="value" fill="#4f46e5" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Porcentajes */}
            <div className="bg-white p-6 rounded-lg shadow mb-6">
                <h2 className="text-xl font-bold mb-4">Tasas de Envío</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <p className="text-gray-600">Tasa de Envío</p>
                        <p className="text-2xl font-bold">{stats.rates?.sentRate?.toFixed(2) || 0}%</p>
                    </div>
                    <div>
                        <p className="text-gray-600">Tasa de Fallos</p>
                        <p className="text-2xl font-bold text-red-600">{stats.rates?.failedRate?.toFixed(2) || 0}%</p>
                    </div>
                    <div>
                        <p className="text-gray-600">Tasa de Entrega</p>
                        <p className="text-2xl font-bold text-green-600">{stats.rates?.deliveryRate?.toFixed(2) || 0}%</p>
                    </div>
                </div>
            </div>

            {/* Timestamps */}
            {stats.timestamps && (
                <div className="bg-white p-6 rounded-lg shadow">
                    <h2 className="text-xl font-bold mb-4">Fechas</h2>
                    <div className="space-y-2">
                        <p><span className="font-medium">Creada:</span> {new Date(stats.timestamps.createdAt).toLocaleString()}</p>
                        {stats.timestamps.sentAt && (
                            <p><span className="font-medium">Enviada:</span> {new Date(stats.timestamps.sentAt).toLocaleString()}</p>
                        )}
                        {stats.timestamps.firstSentAt && (
                            <p><span className="font-medium">Primer envío:</span> {new Date(stats.timestamps.firstSentAt).toLocaleString()}</p>
                        )}
                        {stats.timestamps.lastSentAt && (
                            <p><span className="font-medium">Último envío:</span> {new Date(stats.timestamps.lastSentAt).toLocaleString()}</p>
                        )}
                        {stats.timestamps.completedAt && (
                            <p><span className="font-medium">Completada:</span> {new Date(stats.timestamps.completedAt).toLocaleString()}</p>
                        )}
                    </div>
                </div>
            )}

            {/* Diálogo de Reenvío Inteligente */}
            {showResendDialog && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                        <h3 className="text-xl font-bold mb-4">Reenvío Inteligente</h3>
                        <p className="text-gray-600 mb-4">
                            Se reenviará la campaña a todos los recipients que no abrieron el email original.
                            {engagementStats && (
                                <span className="block mt-2 text-sm font-medium">
                                    Se reenviará a aproximadamente <strong>{engagementStats.totalRecipients - engagementStats.opened}</strong> recipients.
                                </span>
                            )}
                        </p>
                        
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Nuevo Asunto (Opcional)
                            </label>
                            <input
                                type="text"
                                value={newSubject}
                                onChange={(e) => setNewSubject(e.target.value)}
                                placeholder="Ej: ¿Te perdiste nuestra oferta?"
                                className="w-full px-4 py-2 border border-gray-300 rounded"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Si no especificas un nuevo asunto, se usará el asunto original.
                            </p>
                        </div>
                        
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => {
                                    setShowResendDialog(false);
                                    setNewSubject('');
                                }}
                                className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
                                disabled={resending}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleResend}
                                disabled={resending}
                                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {resending ? 'Reenviando...' : 'Reenviar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default CampaignStatsPage;

